import type { ElementType } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";

// @ts-expect-error pure js
import { getBailleurProfileSchema } from "../../models/profile";
// @ts-expect-error pure js
import { getTenantProfileSchema } from "../../models/profile";
// @ts-expect-error pure js
import { PROFILE_INITIAL_VALUE } from "../../models/profile";
// @ts-expect-error pure js
import ProfileFormDom from "./ProfileFormDom";
// @ts-expect-error pure js
import ProfileFormPerso from "./ProfileFormPerso";
// @ts-expect-error pure js
import ProfileFormPro from "./ProfileFormPro";
import Accordion, { AccordionItem } from "../UI/Accordion";
import Avatar from "../UI/Avatar";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import Card from "../UI/Card";
// @ts-expect-error pure js
import FormUpload from "../File/FormUpload";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import ModalDialog from "../UI/ModalDialog";
import Status from "../UI/Status";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
import {
  getDisplayBirthday,
  getDisplayName,
  getInitials,
} from "./profileUtils";
import type { Profile } from "./types";
import styles from "./ProfileForm.module.css";

const SECTION_LABELS: Record<string, string> = {
  perso: "Informations personnelles",
  dom: "Domiciliation",
  pro: "Situation professionnelle",
  documents: "Documents",
};

const SECTION_FIELDS: Record<string, string[]> = {
  perso: [
    "type",
    "mail",
    "prenom",
    "nom",
    "tel",
    "birthday",
    "lieu_naissance",
    "nationalite",
    "nom_societe",
    "socType",
  ],
  dom: ["adresse", "cp", "ville", "pays"],
  pro: [],
  documents: [],
};

interface ProfileFormHeaderProps {
  className?: string;
}

function ProfileFormHeader({ className }: ProfileFormHeaderProps) {
  const { values } = useFormikContext<Profile>();
  const initials = getInitials(values);
  const displayName = getDisplayName(values);
  const birthday = getDisplayBirthday(values);

  return (
    <div className={classnames(styles.header, className)}>
      <Avatar initials={initials || "?"} size="md" />
      <div>
        <Text weight="bold">{displayName || "Nouveau profil"}</Text>
        {parseInt(values.type as string) === 1 && birthday && (
          <Text variant="deci" color="muted">
            Né le {birthday}
          </Text>
        )}
      </div>
    </div>
  );
}

interface ProfileFormSectionsProps {
  profile?: Profile;
  isBailleur?: boolean;
  isGarant?: boolean;
  /** Profil saisi par le candidat lui-même : propose le texte de présentation au bailleur. */
  isCandidat?: boolean;
  withMandatoryFields?: boolean;
  withOldAddressField?: boolean;
  onCancel?: () => void;
  error?: string;
  inline?: boolean;
}

function ProfileFormSections({
  profile,
  isBailleur,
  isGarant,
  isCandidat,
  withMandatoryFields,
  withOldAddressField,
  onCancel,
  error,
  inline,
}: ProfileFormSectionsProps) {
  const {
    values,
    resetForm,
    submitForm,
    isSubmitting,
    errors,
    touched,
    submitCount,
  } = useFormikContext<Profile>();
  const showPro = !isBailleur && parseInt(values.type as string) === 1;
  const showUploads = !isBailleur;

  const sectionOrder = useMemo(() => {
    const order = ["perso", "dom"];
    if (showPro) order.push("pro");
    if (showUploads) order.push("documents");
    return order;
  }, [showPro, showUploads]);

  const [openSection, setOpenSection] = useState<string | null>("perso");
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const prevSubmitCount = useRef(0);

  const errorsByField = errors as Record<string, unknown>;
  const touchedByField = touched as Record<string, unknown>;

  const countSectionErrors = (id: string, requireTouched: boolean) =>
    (SECTION_FIELDS[id] ?? []).filter(
      (f) => errorsByField[f] && (!requireTouched || touchedByField[f]),
    ).length;

  const goToSection = (id: string) => {
    setOpenSection(id);
    requestAnimationFrame(() => {
      sectionRefs.current[id]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  useEffect(() => {
    if (submitCount <= prevSubmitCount.current) {
      prevSubmitCount.current = submitCount;
      return;
    }
    if (isSubmitting) return;
    prevSubmitCount.current = submitCount;
    const firstErrored = sectionOrder.find(
      (id) => countSectionErrors(id, false) > 0,
    );
    if (firstErrored) goToSection(firstErrored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount, isSubmitting]);

  const incompleteSections = sectionOrder.filter(
    (id) => countSectionErrors(id, true) > 0,
  );

  const accordionItemProps = (id: string) => ({
    value: id,
    variant: "filled" as const,
  });

  const renderTitle = (id: string) => {
    const count = countSectionErrors(id, true);
    return (
      <>
        {SECTION_LABELS[id]}
        {count > 0 && <Status color="danger" text={count} as="span" />}
      </>
    );
  };

  const registerSectionRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  const cancelButton = (
    <Button
      type="button"
      variant="secondary"
      onClick={() => {
        resetForm();
        if (onCancel) onCancel();
      }}
    >
      Annuler
    </Button>
  );

  return (
    <>
      <Accordion
        className={styles.sections}
        value={openSection ? [openSection] : []}
        onValueChange={(vals) => setOpenSection((vals[0] as string) ?? null)}
      >
        <div ref={registerSectionRef("perso")}>
          <AccordionItem
            title={renderTitle("perso")}
            {...accordionItemProps("perso")}
          >
            <ProfileFormPerso
              type={values.type}
              isBailleur={isBailleur}
              cautionType={values.caution_type}
              isGarant={isGarant}
              isCandidat={isCandidat}
              profile={profile}
              withMandatoryFields={withMandatoryFields}
              hideTitle
              hideUpload
            />
          </AccordionItem>
        </div>
        <div ref={registerSectionRef("dom")}>
          <AccordionItem
            title={renderTitle("dom")}
            {...accordionItemProps("dom")}
          >
            <ProfileFormDom
              isBailleur={isBailleur}
              withOldAddressField={withOldAddressField}
              hideTitle
              hideUpload
            />
          </AccordionItem>
        </div>
        {showPro && (
          <div ref={registerSectionRef("pro")}>
            <AccordionItem
              title={renderTitle("pro")}
              {...accordionItemProps("pro")}
            >
              <ProfileFormPro hideTitle hideUpload />
            </AccordionItem>
          </div>
        )}
        {showUploads && (
          <div ref={registerSectionRef("documents")}>
            <AccordionItem
              title={renderTitle("documents")}
              {...accordionItemProps("documents")}
            >
              <Text weight="bold" className={styles.uploadLabel}>
                Justificatif d'identité
              </Text>
              <FormUpload tag="identite" shouldWatermark />
              <Text weight="bold" className={styles.uploadLabel}>
                Justificatif de domicile
              </Text>
              <FormUpload tag="domicile" shouldWatermark />
              {showPro && (
                <>
                  <Text weight="bold" className={styles.uploadLabel}>
                    Justificatif de la situation professionnelle
                  </Text>
                  <FormUpload tag="pro" shouldWatermark />
                </>
              )}
            </AccordionItem>
          </div>
        )}
      </Accordion>
      {submitCount > 0 && incompleteSections.length > 0 && (
        <Callout
          variant="danger"
          className={styles.recapCard}
          title="Le formulaire est incomplet. Completez les sections suivantes :"
        >
          <ul className={styles.recapList}>
            {incompleteSections.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  className={styles.recapLink}
                  onClick={() => goToSection(id)}
                >
                  {SECTION_LABELS[id]}
                </button>
              </li>
            ))}
          </ul>
        </Callout>
      )}
      <Callout variant="danger" className={styles.errorCard}>
        {error}
      </Callout>
      {inline ? (
        <div className={styles.actions}>
          {cancelButton}
          <Button
            type="button"
            onClick={() => submitForm()}
            isLoading={isSubmitting}
          >
            Enregistrer
          </Button>
        </div>
      ) : (
        <ModalDialogFooter>
          {cancelButton}
          <FormikSubmitButton>Enregistrer</FormikSubmitButton>
        </ModalDialogFooter>
      )}
    </>
  );
}

interface ProfileFormProps {
  profile?: Profile;
  /** Ancienne modale (`useModal`). Préférer `open` / `onOpenChange`. */
  modal?: ElementType;
  /** Mode ModalDialog : le formulaire porte sa propre modale, pilotée par le parent. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  inline?: boolean;
  isGarant?: boolean;
  isBailleur?: boolean;
  isCandidat?: boolean;
  withMandatoryFields?: boolean;
  onSubmit: (data: Profile) => Promise<void> | void;
  onCancel?: () => void;
  withOldAddressField?: boolean;
}

export default function ProfileForm({
  profile,
  modal: ModalElement,
  open = false,
  onOpenChange,
  inline = false,
  isGarant,
  isBailleur,
  isCandidat,
  withMandatoryFields,
  onSubmit,
  onCancel,
  withOldAddressField,
}: ProfileFormProps) {
  const [error, setError] = useState("");

  let validationSchema = getTenantProfileSchema(!!withMandatoryFields);
  if (isBailleur) {
    validationSchema = getBailleurProfileSchema(!!withMandatoryFields);
  }
  const initialValues = toInitialValuesFromSchema(
    validationSchema,
    profile,
    PROFILE_INITIAL_VALUE,
  );

  async function submitHandler(
    values: Profile,
    { resetForm }: FormikHelpers<Profile>,
  ) {
    const data = {
      ...sanitizeValues(values),
    };

    try {
      await onSubmit(data);
      resetForm();
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  return (
    <Formik<Profile>
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={submitHandler}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        const sections = (
          <ProfileFormSections
            profile={profile}
            isBailleur={isBailleur}
            isGarant={isGarant}
            isCandidat={isCandidat}
            withMandatoryFields={withMandatoryFields}
            withOldAddressField={withOldAddressField}
            onCancel={onCancel}
            error={error}
            inline={inline}
          />
        );

        if (inline) {
          // Inline mode: no <form> element to avoid nested HTML forms
          // (caller is already inside a Formik form). Submit is wired via
          // explicit onClick on the Enregistrer button.
          return (
            <Card padding="xs" className={styles.inlineCard}>
              <ProfileFormHeader />
              {sections}
            </Card>
          );
        }

        if (onOpenChange) {
          const birthday = getDisplayBirthday(formikProps.values);
          return (
            <ModalDialog
              open={open}
              size="md"
              noBackdropClose
              header={getDisplayName(formikProps.values) || "Nouveau profil"}
              subtitle={birthday ? `Né le ${birthday}` : undefined}
              onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                  formikProps.resetForm();
                  setError("");
                }
                onOpenChange(nextOpen);
              }}
            >
              <Form>{sections}</Form>
            </ModalDialog>
          );
        }

        if (!ModalElement) return null;
        return (
          <ModalElement
            stickyHeader
            confirmCloseText={
              "Votre formulaire n'a pas été sauvegardé. Voulez-vous vraiment quitter ?"
            }
            shouldConfirm={() => formikProps.dirty}
            size={"medium"}
            blueHeader
            header={<ProfileFormHeader />}
          >
            <Form>{sections}</Form>
          </ModalElement>
        );
      }}
    </Formik>
  );
}
